/**
 * Primitivas de fecha y hora sin zona horaria, equivalentes a `LocalDate`/`LocalTime` de la app
 * Android.
 *
 * Se representan como cadenas (`"2026-08-31"`, `"20:30"`) y no como `Date`, a propósito: `Date`
 * es siempre un instante en UTC y arrastra la zona del navegador a cada comparación, que es
 * justo el error que produce reservas desplazadas una hora. Aquí una fecha es una fecha civil
 * y una hora es una hora de reloj, igual que en el contrato del servidor.
 */

/** `YYYY-MM-DD`. */
export type LocalDate = string;
/** `HH:mm`. */
export type LocalTime = string;
/** `YYYY-MM-DDTHH:mm`. */
export type LocalDateTime = string;

/** Lunes = 1 … Domingo = 7, igual que `java.time.DayOfWeek.value`. */
export type DayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

const pad = (n: number) => String(n).padStart(2, '0');

export function isValidDate(value: string | null | undefined): value is LocalDate {
  if (!value || !DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= daysInMonth(y, m);
}

export function isValidTime(value: string | null | undefined): value is LocalTime {
  if (!value || !TIME_RE.test(value)) return false;
  const [h, m] = value.split(':').map(Number);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Fecha civil de hoy según el reloj local del navegador. */
export function today(now: Date = new Date()): LocalDate {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Hora de reloj actual, redondeada hacia arriba al múltiplo de 30 minutos. */
export function nowTime(now: Date = new Date()): LocalTime {
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export function nowDateTime(now: Date = new Date()): LocalDateTime {
  return dateTimeOf(today(now), nowTime(now));
}

export function dateTimeOf(date: LocalDate, time: LocalTime): LocalDateTime {
  return `${date}T${time}`;
}

export function dateOf(dateTime: LocalDateTime): LocalDate {
  return dateTime.slice(0, 10);
}

export function timeOf(dateTime: LocalDateTime): LocalTime {
  return dateTime.slice(11, 16);
}

/**
 * Compara dos valores del mismo tipo. Al estar en formato ISO de ancho fijo, el orden
 * lexicográfico coincide con el cronológico, así que no hace falta construir objetos `Date`.
 */
export function compare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function isBefore(a: string, b: string): boolean {
  return a < b;
}

export function isAfter(a: string, b: string): boolean {
  return a > b;
}

function toUtc(date: LocalDate): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function plusDays(date: LocalDate, days: number): LocalDate {
  const base = toUtc(date);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

export function minusDays(date: LocalDate, days: number): LocalDate {
  return plusDays(date, -days);
}

/** Días naturales entre dos fechas (`to - from`). */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000);
}

export function minutesOfDay(time: LocalTime): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function timeFromMinutes(totalMinutes: number): LocalTime {
  // El módulo positivo hace que sumar o restar minutos cruce la medianoche sin producir
  // horas negativas ni mayores de 23.
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
}

export function plusMinutes(time: LocalTime, minutes: number): LocalTime {
  return timeFromMinutes(minutesOfDay(time) + minutes);
}

/**
 * Minutos entre dos instantes civiles. Positivo cuando `to` es posterior.
 * Ignora la zona horaria en ambos extremos, que es lo correcto para comparar la hora que el
 * restaurante escribió en su horario con la que el usuario está eligiendo.
 */
export function minutesBetween(from: LocalDateTime, to: LocalDateTime): number {
  const days = daysBetween(dateOf(from), dateOf(to));
  return days * 1440 + minutesOfDay(timeOf(to)) - minutesOfDay(timeOf(from));
}

export function plusHours(dateTime: LocalDateTime, hours: number): LocalDateTime {
  return plusMinutesDateTime(dateTime, hours * 60);
}

export function plusMinutesDateTime(dateTime: LocalDateTime, minutes: number): LocalDateTime {
  const total = minutesOfDay(timeOf(dateTime)) + minutes;
  const dayShift = Math.floor(total / 1440);
  return dateTimeOf(plusDays(dateOf(dateTime), dayShift), timeFromMinutes(total));
}

/** Lunes = 1 … Domingo = 7. */
export function dayOfWeek(date: LocalDate): DayOfWeek {
  const jsDay = toUtc(date).getUTCDay(); // 0 = domingo
  return (jsDay === 0 ? 7 : jsDay) as DayOfWeek;
}
